import type { UnaryCall } from '@protobuf-ts/runtime-rpc'
import { K2ResponseType } from 'shared/utils/errors'
import logger, { ErrorOptions } from '../../logger'
import { processK2Error, processK2Response } from '../client-utils'
import { GuaranteedResponse, RequiredK2Response } from './k2-use-case.schema'
import { generateSuccessK2Response } from 'shared/utils/errors/k2-error.helper'

type UseCaseOptions<T> = {
  loggerOptions: ErrorOptions | ((request: T) => ErrorOptions)
  defaultErrorMessage: string
}

type InnerCallback<
  RequestInput extends object,
  ResponseType extends K2ResponseType<object>,
> = (request: RequestInput) => UnaryCall<object, ResponseType>

type OuterCallback<
  RequestInput extends object,
  ResponseType extends K2ResponseType<object>,
> = (
  request: RequestInput,
) => Promise<GuaranteedResponse<RequiredK2Response<ResponseType>['response']>>

/**
 * @deprecated # use `K2ServerActionUseCase` instead
 *
 * This method wraps a grpc-call to k2 and processes the response and error
 * - Errors caught are logged and re-thrown as K2GenericErrors, with user-friendly messages
 * - Responses are processed to be used as GuaranteedResponses
 */
export default function K2UseCase<
  RequestInput extends object,
  ResponseType extends K2ResponseType<object>,
>(
  callback: InnerCallback<RequestInput, ResponseType>,
  { loggerOptions, defaultErrorMessage }: UseCaseOptions<RequestInput>,
): OuterCallback<RequestInput, ResponseType> {
  return async (
    request: RequestInput,
  ): Promise<
    GuaranteedResponse<RequiredK2Response<ResponseType>['response']>
  > => {
    try {
      const response = await callback(request)
      return processK2Response(response)
    } catch (error) {
      logger.error(
        error,
        typeof loggerOptions === 'function'
          ? loggerOptions(request)
          : loggerOptions,
      )
      throw processK2Error(error, defaultErrorMessage)
    }
  }
}

/**
 *  A helper method to mock a grpc-call for testing when the actual grpc call has not been generated yet
 * - used within a K2UseCase
 */
function mockedResponse<ResponseType extends K2ResponseType<object>>(
  response?: ResponseType,
): UnaryCall<any, ResponseType> {
  return generateSuccessK2Response<ResponseType>(
    response,
  ) as unknown as UnaryCall<any, ResponseType>
}

K2UseCase.mockedResponse = mockedResponse
